'use client';
// components/modals/LogoutConfirmModal.tsx
import React from 'react';
import { usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
} from '@/components';
import { useBasketStore, useLogout, useTranslation } from '@/hooks';
import useIsAdmin from '@/hooks/store/useIsAdmin';
import { privateRoutPrefixes, routes } from '@/statics';
import { deleteCookie, deleteLocalStorage } from '@/utils';

type LogoutConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};

const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('components.header.logoutModal');
  const pathname = usePathname();
  const { mutateAsync: logout, isPending } = useLogout();
  const emptyBasket = useBasketStore(useShallow((state) => state.empty));
  const toggleUserType = useIsAdmin(
    useShallow((state) => state.toggleUserType)
  );
  const [navigateLoading, setNavigateLoading] = React.useState(false);

  const isPrivateRoute = privateRoutPrefixes.some((prefix) =>
    pathname.startsWith(prefix)
  );

  const handleLogout = () => {
    setNavigateLoading(true);
    logout({})
      .then(async () => {
        await deleteCookie({ name: 'lia-token' });
        deleteLocalStorage({ name: 'lia-token' });
        emptyBasket();
        toggleUserType(false);
        if (isPrivateRoute) {
          window.location.replace(routes.signIn.url);
        } else {
          window.location.replace(pathname);
        }
        onClose();
        onConfirm?.();
      })
      .catch(() => setNavigateLoading(false));
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent w="650px">
        <ModalBody p={8}>
          <Text textAlign="center" color="text">
            {t('logoutWarning')}
          </Text>
          <Stack
            flexDir="row"
            align="center"
            justify="center"
            marginTop="30px"
            gap="25px"
          >
            <Button onClick={onClose} colorScheme="gray" variant="outline">
              {t('cancel')}
            </Button>
            <Button
              isLoading={isPending || navigateLoading}
              colorScheme="secondary"
              onClick={handleLogout}
            >
              {t('submit')}
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LogoutConfirmModal;
